/**
 * Indian-numbering word converter — used on quotation + proforma invoice
 * detail pages so the on-screen "Amount in Words" line matches the
 * downloaded PDF exactly.
 *
 * Example:
 *   inrInWords(123450) → 'Rupees One Lakh Twenty-Three Thousand Four Hundred Fifty Only'
 *   inrInWords(10000000) → 'Rupees One Crore Only'
 *
 * Rounds to the nearest whole rupee (paise dropped per Feb 2026 user
 * preference — matches the backend `inr_in_words` behaviour).
 */

const ONES = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen',
  'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

function twoDigit(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return TENS[t] + (o ? `-${ONES[o]}` : '');
}

function threeDigit(n) {
  if (n === 0) return '';
  const h = Math.floor(n / 100);
  const rem = n % 100;
  const parts = [];
  if (h) parts.push(`${ONES[h]} Hundred`);
  if (rem) parts.push(twoDigit(rem));
  return parts.join(' ');
}

export function inrInWords(value) {
  const raw = Number(value || 0);
  if (!Number.isFinite(raw)) return 'Rupees Zero Only';
  let n = Math.round(Math.abs(raw));
  const neg = raw < 0;
  if (n === 0) return 'Rupees Zero Only';

  const crore = Math.floor(n / 10_000_000);  n %= 10_000_000;
  const lakh = Math.floor(n / 100_000);      n %= 100_000;
  const thousand = Math.floor(n / 1_000);
  const last3 = n % 1_000;

  const parts = [];
  if (crore) {
    if (crore >= 100) {
      // Handle 100+ Crore (rare, but keep the words correct)
      const higher = Math.floor(crore / 1000);
      const midCrore = crore % 1000;
      if (higher) parts.push(`${threeDigit(higher)} Thousand Crore`);
      if (midCrore) parts.push(`${threeDigit(midCrore)} Crore`);
    } else {
      parts.push(`${twoDigit(crore)} Crore`);
    }
  }
  if (lakh) parts.push(`${twoDigit(lakh)} Lakh`);
  if (thousand) parts.push(`${twoDigit(thousand)} Thousand`);
  if (last3) parts.push(threeDigit(last3));

  const words = parts.filter(Boolean).join(' ');
  return `${neg ? 'Minus ' : ''}Rupees ${words} Only`;
}
